import type {
  AdminDoerGroupKind,
  AdminCoachingSessionDetail,
  AdminCoachingSessionsResponse,
  AdminDslAgentResponse,
  AdminOnboardingResetResult,
  AdminOnboardingUnblockResult,
  AdminOnboardingView,
  OnboardingStepNumber,
  OnboardingTransitionResult,
  AdminEscalationAssigneesResponse,
  EscalationHistoryResponse,
  AdminEscalationDetail,
  AdminEscalationsResponse,
  AdminUser,
  AdminUsersListResponse,
  AdminWeeklyReviewDetail,
  AdminWeeklyReviewsResponse,
  AgentDeviceListResponse,
  AgentDeviceRevokeResponse,
  AgentEnrolmentGapsResponse,
  AgentVersionDistributionResponse,
  AssignableDoerRecord,
  BlueprintStep,
  BusinessBrief,
  BusinessBriefData,
  CategoryRecommendedPlaybookWithPlaybook,
  CategoryWithRecommendedPlaybooks,
  CoachingSessionStatus,
  CreateDoerGroupInput,
  CreateGroupChatInput,
  CxRiskRelationship,
  DeactivationImpact,
  DoerGroupDetail,
  DoerGroupListItem,
  DoerGroupMember,
  DslTeamSyncLatest,
  GroupChatDetail,
  GroupChatListItem,
  UpdateGroupChatInput,
  EscalationConsoleResponse,
  EscalationQueueFilters,
  EscalationStatus,
  ExecStats,
  FinOpsStats,
  PilotStats,
  FounderCategory,
  FounderCategoryCreateInput,
  FounderCategoryUpdateInput,
  FounderRiskBand,
  UpdateDoerGroupInput,
  MemberRemovalImpact,
  Playbook,
  PlaybookReassignment,
  PlaybookSchema,
  SensitivityProfile,
  Team,
  TeamMember,
  TeamMemberWithUser,
  User,
  UserActivity,
  UserRole,
  WeeklyReviewContent,
  WorkingHours,
} from '@doneverse/shared'
import { AGENT_REQUIRED_ROLES, DOER_GROUP_ROLE_RULES, ONBOARDING_RESET_CONFIRM } from '@doneverse/shared'
import { api } from '../api/index.js'
import { isNotFoundError } from '../api/index.js'
import { nestSchema } from '../utils/index.js'
import type { SchemaField } from '../utils/index.js'

const ASSIGNABLE_DOERS_SEARCH_LIMIT = 20

type PickerRole = Extract<
  UserRole,
  'doer' | 'dsl' | 'doing_team' | 'founder' | 'escalation_officer' | 'admin' | 'supervisor' | 'support_staff'
>

/**
 * Active users for the assignable-user pickers, optionally narrowed to one role
 * or any of several. Omitting `role` searches every role — what a picker whose
 * target edge is role-neutral (group-chat membership) needs.
 */
async function searchActiveProfiles(
  search?: string,
  role?: PickerRole | readonly PickerRole[],
  limit = ASSIGNABLE_DOERS_SEARCH_LIMIT,
  /** `relevance`: closest matches first (ClickUp 14zf25zmr34). Keep that order;
   *  re-sorting A–Z would undo it. */
  sort?: 'relevance',
) {
  const roles = role === undefined ? [] : [role].flat()
  const roleParam = roles.length > 0 ? `role=${roles.join(',')}&` : ''
  const searchParam = search?.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''
  const sortParam = sort ? `&sort=${sort}` : ''
  // active=true: assignable-user pickers must never surface inactive (disabled)
  // users — the API also rejects assigning them, this keeps them out of the list.
  const response = await api
    .get(`/v1/admin/users?${roleParam}active=true&page=1&limit=${limit}${searchParam}${sortParam}`)
    .json<AdminUsersListResponse>()

  return response.users.map((user) => ({
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
  }))
}

/** Picker ordering: by the label the combobox actually renders. */
function byDisplayLabel(left: AssignableDoerRecord, right: AssignableDoerRecord): number {
  return (left.name || left.email).localeCompare(right.name || right.email)
}

export const adminService = {
  // Admin Page Data Fetching
  fetchUser: async (userId: string): Promise<AdminUser> => {
    return api.get(`/v1/admin/users/${userId}`).json<AdminUser>()
  },

  fetchFounderBusinessBrief: async (founderId: string): Promise<BusinessBriefData | null> => {
    if (!founderId) return null
    try {
      const row = await api
        .get(`/v1/business-brief?user_id=${encodeURIComponent(founderId)}`)
        .json<BusinessBrief>()
      return (row.data as BusinessBriefData) ?? null
    } catch (error: unknown) {
      if (isNotFoundError(error)) return null
      throw error
    }
  },

  fetchProfiles: async (
    page = 1,
    limit = 20,
    search?: string,
    unassigned?: boolean,
    sort?: string,
    order?: 'asc' | 'desc',
    active?: boolean,
    isTest?: boolean,
  ): Promise<AdminUsersListResponse> => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : ''
    // API requires role filter when unassigned=true.
    const roleParam = unassigned ? '&role=founder' : ''
    const unassignedParam = unassigned ? `&unassigned=true` : ''
    const sortParam = sort ? `&sort=${encodeURIComponent(sort)}` : ''
    const orderParam = order ? `&order=${order}` : ''
    const activeParam = active === undefined ? '' : `&active=${active}`
    const isTestParam = isTest === undefined ? '' : `&is_test=${isTest}`
    return await api
      .get(
        `/v1/admin/users?page=${page}&limit=${limit}${searchParam}${roleParam}${unassignedParam}${sortParam}${orderParam}${activeParam}${isTestParam}`,
      )
      .json<AdminUsersListResponse>()
  },

  searchAssignableDoers: async (search?: string): Promise<AssignableDoerRecord[]> => {
    const [doers, doingTeams] = await Promise.all([
      searchActiveProfiles(search, 'doer'),
      searchActiveProfiles(search, 'doing_team'),
    ])

    const usersById = new Map<string, AssignableDoerRecord>()
    for (const user of [...doers, ...doingTeams]) {
      usersById.set(user.id, user)
    }

    return Array.from(usersById.values()).sort(byDisplayLabel)
  },

  /** Founders that can be assigned to a doer (doer-detail page picker). */
  searchAssignableFounders: async (search?: string): Promise<AssignableDoerRecord[]> => {
    return (await searchActiveProfiles(search, 'founder')).sort(byDisplayLabel)
  },

  // Candidate owners for a doer group of this kind: exactly the roles the API
  // lets own it (DOER_GROUP_ROLE_RULES), so a pick is never refused.
  searchDoerGroupOwners: async (
    kind: AdminDoerGroupKind,
    search?: string,
  ): Promise<AssignableDoerRecord[]> => {
    return (await searchActiveProfiles(search, DOER_GROUP_ROLE_RULES[kind].owners)).sort(byDisplayLabel)
  },

  // Admins — candidate owners for a feedback item. Every item gets an owner, so
  // that two admins never work the same one (brief §5.2, step 4).
  searchAssignableAdmins: async (search?: string): Promise<AssignableDoerRecord[]> => {
    return (await searchActiveProfiles(search, 'admin')).sort(byDisplayLabel)
  },

  // Candidate members of a *doer group* of this kind, exactly the roles its API
  // accepts (DOER_GROUP_ROLE_RULES): doers for a pod; support staff, doers and
  // DSLs for a support team. Group *chats* are role-neutral and use
  // searchGroupChatMembers instead.
  searchDoerGroupMembers: async (
    kind: AdminDoerGroupKind,
    search?: string,
  ): Promise<AssignableDoerRecord[]> => {
    return (await searchActiveProfiles(search, DOER_GROUP_ROLE_RULES[kind].members)).sort(byDisplayLabel)
  },

  // Everyone who runs DoneFlow (AGENT_REQUIRED_ROLES: doers, DSLs and support staff, ClickUp
  // 14zf25zn6na) for the Activity Log picker, closest match first and NOT
  // re-sorted: the server ranks across every active match, which a sort of the
  // returned page cannot (ClickUp 14zf25zmr34).
  searchActivityLogDoers: async (search: string): Promise<AssignableDoerRecord[]> => {
    return searchActiveProfiles(search, AGENT_REQUIRED_ROLES, ASSIGNABLE_DOERS_SEARCH_LIMIT, 'relevance')
  },

  // Every active user, any role — candidate members of an independent group
  // chat. Membership there is role-neutral all the way down (schema, RPC and
  // API), so the picker offers admins and founders alongside doers.
  searchGroupChatMembers: async (search?: string): Promise<AssignableDoerRecord[]> => {
    return (await searchActiveProfiles(search)).sort(byDisplayLabel)
  },

  /**
   * Assign a doer to a founder from the doer's side. Resolves/creates the
   * founder's workspace server-side and adds the doer as a member — the
   * multi-founder-safe inverse of adding a doer on the founder page.
   */
  assignFounderToDoer: async (doerId: string, founderUserId: string): Promise<void> => {
    await api.post({ founder_user_id: founderUserId }, `/v1/admin/users/${doerId}/founders`).res()
  },

  createUser: async (body: {
    email: string
    name: string
    role: UserRole
    founder_category?: string
    doer_id?: string
    send_invite?: boolean
    /** Founders only — they signed outside the system, so skip onboarding step 1. */
    agreement_signed?: boolean
  }): Promise<{
    user_id: string
    email: string
    name: string
    role: UserRole
    invite_token: string
    invite_url: string
    invitation_sent: boolean
    agreement_signed: boolean
  }> => {
    return api.post(body, '/v1/admin/users').json()
  },

  getInviteLink: async (
    userId: string,
  ): Promise<{
    invite_url: string
    invite_token: string
    email: string
    name: string
    invite_sent_at: string | null
  }> => {
    return api.get(`/v1/admin/users/${userId}/invite-link`).json()
  },

  /**
   * Admin-managed founder categories, ordered by display_order. Archived
   * categories are excluded unless asked for: they stay readable for history
   * but can no longer be assigned to a founder.
   */
  fetchFounderCategories: async (includeArchived = false): Promise<FounderCategory[]> => {
    const query = includeArchived ? '?include_archived=true' : ''
    return api.get(`/v1/admin/founders/categories${query}`).json<FounderCategory[]>()
  },

  fetchDoingTeams: async (): Promise<Team[]> => {
    return api.get('/v1/teams').json<Team[]>()
  },

  fetchStats: async (): Promise<ExecStats> => {
    return api.get('/v1/admin/stats').json<ExecStats>()
  },

  fetchFinOpsStats: async (): Promise<FinOpsStats> => {
    return api.get('/v1/admin/stats/finops').json<FinOpsStats>()
  },

  fetchPilotStats: async (): Promise<PilotStats> => {
    return api.get('/v1/admin/stats/pilot').json<PilotStats>()
  },

  fetchAgentVersionDistribution: async (): Promise<AgentVersionDistributionResponse> => {
    return api.get('/v1/admin/stats/agent-versions').json<AgentVersionDistributionResponse>()
  },

  // No staleDays: it only shapes the legacy `gaps`/`totals`, which nothing
  // reads any more, and the API defaults it.
  fetchAgentEnrolmentGaps: async (): Promise<AgentEnrolmentGapsResponse> => {
    return api.get('/v1/admin/stats/agent-enrolment').json<AgentEnrolmentGapsResponse>()
  },

  // The Feedback Queue lives in feedbackService — it needs the full triage
  // filter set, not just a type, so it isn't a one-liner on the admin service.

  // Coaching session inspector (read-only, admin-scoped)
  fetchCoachingSessions: async (
    status?: CoachingSessionStatus,
    limit = 50,
    doerId?: string,
  ): Promise<AdminCoachingSessionsResponse> => {
    const statusParam = status ? `&status=${status}` : ''
    const doerParam = doerId ? `&doer_id=${doerId}` : ''
    return api
      .get(`/v1/admin/coaching-sessions?limit=${limit}${statusParam}${doerParam}`)
      .json<AdminCoachingSessionsResponse>()
  },

  fetchCoachingSession: async (id: string): Promise<AdminCoachingSessionDetail> => {
    return api.get(`/v1/admin/coaching-sessions/${id}`).json<AdminCoachingSessionDetail>()
  },

  resetCoachingSession: async (id: string): Promise<AdminCoachingSessionDetail['session']> => {
    const res = await api
      .post({}, `/v1/admin/coaching-sessions/${id}/reset`)
      .json<{ session: AdminCoachingSessionDetail['session'] }>()
    return res.session
  },

  // DSL doer-coach agent state (read-only, admin-scoped)
  fetchDslAgent: async (): Promise<AdminDslAgentResponse> => {
    return api.get('/v1/admin/dsl-agent').json<AdminDslAgentResponse>()
  },

  /**
   * CX Risk Dashboard list — the latest founder-relationship risk score per
   * (workspace, doer) relationship across the platform, names joined. Gated to
   * admin + escalation_officer (note: no `/v1` prefix, like the founder-risk
   * routes). Optional band filter narrows to the relationship's current band.
   */
  fetchCxRiskRelationships: async (band?: FounderRiskBand): Promise<CxRiskRelationship[]> => {
    const query = band ? `?band=${band}` : ''
    return api.get(`/admin/cx-risk${query}`).json<CxRiskRelationship[]>()
  },

  // Escalation-officer queue (gated to admin + escalation_officer)
  fetchEscalations: async (filters: EscalationQueueFilters = {}): Promise<AdminEscalationsResponse> => {
    const { limit = 50, status, severity, assigned_to, unassigned, sort } = filters
    const params = new URLSearchParams({ limit: String(limit) })
    if (status) params.set('status', status)
    if (severity) params.set('severity', severity)
    if (assigned_to) params.set('assigned_to', assigned_to)
    if (unassigned) params.set('unassigned', 'true')
    if (sort) params.set('sort', sort)
    return api.get(`/v1/admin/escalations?${params.toString()}`).json<AdminEscalationsResponse>()
  },

  fetchEscalation: async (id: string): Promise<AdminEscalationDetail> => {
    return api.get(`/v1/admin/escalations/${id}`).json<AdminEscalationDetail>()
  },

  fetchEscalationHistory: async (id: string): Promise<EscalationHistoryResponse> => {
    return api.get(`/v1/admin/escalations/${id}/history`).json<EscalationHistoryResponse>()
  },

  fetchEscalationAssignees: async (): Promise<AdminEscalationAssigneesResponse> => {
    return api.get('/v1/admin/escalations/assignees').json<AdminEscalationAssigneesResponse>()
  },

  /**
   * Claim, reassign, or unclaim (null). Moving an item off another user's
   * ownership needs `takeOver` — without it the API answers 409, which is what
   * keeps a stray click from silently pulling work off another officer.
   */
  assignEscalation: async (
    id: string,
    assignedTo: string | null,
    takeOver = false,
  ): Promise<AdminEscalationDetail> => {
    return api
      .patch(
        { assigned_to: assignedTo, ...(takeOver ? { take_over: true } : {}) },
        `/v1/admin/escalations/${id}/assign`,
      )
      .json<AdminEscalationDetail>()
  },

  /** Move along open → acknowledged → resolved; resolving captures notes + stamps resolved_at. */
  updateEscalationStatus: async (
    id: string,
    status: EscalationStatus,
    resolutionNotes?: string | null,
  ): Promise<AdminEscalationDetail> => {
    return api
      .patch({ status, resolution_notes: resolutionNotes ?? undefined }, `/v1/admin/escalations/${id}/status`)
      .json<AdminEscalationDetail>()
  },

  /**
   * Escalation Officer console — pod roster + scoped escalation queue + stats
   * for the acting officer's owned groups. Gated to escalation_officer + admin.
   */
  fetchEscalationConsole: async (): Promise<EscalationConsoleResponse> => {
    return api.get('/v1/me/escalation-console').json<EscalationConsoleResponse>()
  },

  // Weekly Review reviewer queue (gated to admin + escalation_officer). Amber/red
  // recaps land in `pending_review` and only reach the founder once approved here.
  fetchPendingWeeklyReviews: async (limit: number): Promise<AdminWeeklyReviewsResponse> => {
    return api.get(`/v1/admin/weekly-reviews/pending?limit=${limit}`).json<AdminWeeklyReviewsResponse>()
  },

  fetchWeeklyReview: async (id: string): Promise<AdminWeeklyReviewDetail> => {
    return api.get(`/v1/admin/weekly-reviews/${id}`).json<AdminWeeklyReviewDetail>()
  },

  /**
   * Replaces the whole recap blob — send the full content, not just the edited
   * sections, or the untouched ones are dropped. Audited server-side; 409s once
   * the review is no longer pending.
   */
  updateWeeklyReviewContent: async (
    id: string,
    content: WeeklyReviewContent,
  ): Promise<AdminWeeklyReviewDetail> => {
    return api.patch({ content }, `/v1/admin/weekly-reviews/${id}`).json<AdminWeeklyReviewDetail>()
  },

  /** Publishes the recap and notifies the founder in-app. 409s if not pending. */
  approveWeeklyReview: async (id: string): Promise<AdminWeeklyReviewDetail> => {
    return api.post({}, `/v1/admin/weekly-reviews/${id}/approve`).json<AdminWeeklyReviewDetail>()
  },

  // Admin Page Actions
  updateUserRole: async (userId: string, newRole: UserRole): Promise<User> => {
    return api.patch({ role: newRole }, `/v1/admin/users/${userId}`).json<User>()
  },

  addWorkspaceMember: async (workspaceId: string, userId: string): Promise<void> => {
    await api.post({ user_id: userId }, `/v1/admin/workspaces/${workspaceId}/members`).res()
  },

  /**
   * Impact preview for removing a member: the active playbooks they own and the
   * other doers who could take them over. Drives the reassign-in-flow dialog.
   */
  getMemberRemovalImpact: async (workspaceId: string, userId: string): Promise<MemberRemovalImpact> => {
    return api.get(`/v1/admin/workspaces/${workspaceId}/members/${userId}/impact`).json<MemberRemovalImpact>()
  },

  /**
   * Remove a member. `reassignments` accounts for every active playbook the
   * member owns — each is reassigned to another doer or explicitly left
   * unassigned (`doer_id: null`). The API rejects removal (409) if an active
   * playbook is left uncovered.
   */
  removeWorkspaceMember: async (
    workspaceId: string,
    userId: string,
    reassignments: PlaybookReassignment[] = [],
  ): Promise<void> => {
    await api.json({ reassignments }).delete(`/v1/admin/workspaces/${workspaceId}/members/${userId}`).res()
  },

  updateFounderBusinessBrief: async (
    founderId: string,
    businessBrief: BusinessBriefData,
  ): Promise<BusinessBriefData> => {
    if (!founderId) {
      throw new Error('Founder ID is required to update the business brief.')
    }
    const row = await api
      .put({ data: businessBrief }, `/v1/business-brief?user_id=${encodeURIComponent(founderId)}`)
      .json<BusinessBrief>()
    return row.data as BusinessBriefData
  },

  updateUserFields: async (
    userId: string,
    fields: {
      name?: string
      email?: string
      role?: UserRole
      founder_category?: string | null
      doer_id?: string | null
      company_name?: string | null
      logo_url?: string | null
      working_hours?: WorkingHours | null
      sensitivity_profile?: SensitivityProfile | null
      is_test?: boolean
    },
  ): Promise<User> => {
    return api.patch(fields, `/v1/admin/users/${userId}`).json<User>()
  },

  deleteUser: async (userId: string): Promise<void> => {
    // API returns 204 No Content
    await api.delete(`/v1/admin/users/${userId}`).res()
  },

  /**
   * Preview the impact of disabling a user (in-flight playbooks unassigned, pods
   * left, workspaces touched) so the confirmation dialog can show real numbers
   * before the admin commits. Founders/non-doers come back with zero counts.
   */
  fetchDeactivationImpact: async (userId: string): Promise<DeactivationImpact> => {
    return api.get(`/v1/admin/users/${userId}/deactivation-impact`).json<DeactivationImpact>()
  },

  /**
   * Enable/disable a user (global access flag). Disabling blocks login and, for
   * doers, cascades: pass confirmUnassign=true to also unassign their in-flight
   * playbooks (the API returns 409 cascade_required otherwise). The UI shows the
   * impact up front, so it confirms in a single call.
   */
  setUserActive: async (userId: string, active: boolean, confirmUnassign = false): Promise<User> => {
    return api
      .patch(
        { is_active: active, ...(active ? {} : { confirm_unassign: confirmUnassign }) },
        `/v1/admin/users/${userId}`,
      )
      .json<User>()
  },

  resendInvitation: async (email: string, name: string, role: string): Promise<void> => {
    // Reuses the invite endpoint which handles existing users by resending the welcome email
    await api.post({ email, name, role }, '/v1/admin/users/invite').json()
  },

  /**
   * Set a random temporary password for a user directly in Auth0 and return it for
   * the admin to share securely. Use when password reset emails aren't reaching the
   * user. If the user hasn't logged in yet, the API creates their Auth0 (password)
   * account on demand. Rejected by the API for social-login users.
   */
  setTempPassword: async (email: string): Promise<{ temp_password: string }> => {
    return api
      .post({ email, create_temp_password: true }, '/v1/admin/users/invite')
      .json<{ temp_password: string }>()
  },

  // BriefSchemaEditor
  fetchPlaybookList: async (all = false): Promise<Playbook[]> => {
    return api.get(`/playbooks${all ? '?all=true' : ''}`).json<Playbook[]>()
  },

  fetchPlaybookBySlug: async (slug: string): Promise<Playbook> => {
    return api.get(`/playbooks/${slug}`).json<Playbook>()
  },

  updatePlaybookFounderSchema: async (playbookId: string, flatSchema: SchemaField[]): Promise<Playbook> => {
    const nestedSchema = nestSchema(flatSchema)
    return api.patch({ schema: nestedSchema }, `/playbooks/${playbookId}`).json<Playbook>()
  },

  fetchPlaybookDoerSchema: async (slug: string): Promise<PlaybookSchema> => {
    const playbook = await api.get(`/playbooks/${slug}`).json<Playbook>()
    return playbook.doer_schema ?? []
  },

  updatePlaybookDoerSchema: async (playbookId: string, flatSchema: SchemaField[]): Promise<void> => {
    const nestedSchema = nestSchema(flatSchema)
    await api.patch({ doer_schema: nestedSchema }, `/playbooks/${playbookId}`).json()
  },

  // Team member management
  fetchTeamMembersForTeam: async (teamId: string): Promise<TeamMemberWithUser[]> => {
    return api.get(`/v1/teams/${teamId}/members`).json<TeamMemberWithUser[]>()
  },

  fetchDoerProfiles: async (): Promise<AdminUser[]> => {
    try {
      const res = await api.get('/v1/admin/users?role=doing_team').json<AdminUsersListResponse>()
      return res.users
    } catch {
      return []
    }
  },

  addTeamMember: async (teamId: string, userId: string): Promise<TeamMember> => {
    return api.post({ user_id: userId }, `/v1/teams/${teamId}/members`).json<TeamMember>()
  },

  removeTeamMember: async (teamId: string, userId: string): Promise<void> => {
    await api.delete(`/v1/teams/${teamId}/members/${userId}`).res()
  },

  // PlaybookManager (uses same list endpoint as BriefSchemaEditor)

  fetchBlueprintSteps: async (): Promise<Pick<BlueprintStep, 'id' | 'title' | 'step_number'>[]> => {
    return api.get('/v1/blueprint/steps').json<Pick<BlueprintStep, 'id' | 'title' | 'step_number'>[]>()
  },

  fetchActiveDoingTeams: async (): Promise<Team[]> => {
    return api.get('/v1/teams').json<Team[]>()
  },

  updateMasterPlaybook: async (id: string, data: Partial<Playbook>): Promise<Playbook> => {
    return api.patch(data, `/playbooks/${id}`).json<Playbook>()
  },

  createMasterPlaybook: async (data: Partial<Playbook>): Promise<Playbook> => {
    return api.post({ schema: [], ...data }, '/playbooks').json<Playbook>()
  },

  createTeam: async (name: string, clickupListId: string): Promise<Team> => {
    return api.post({ name, clickup_list_id: clickupListId }, '/v1/teams').json<Team>()
  },

  // Doer groups (escalation-officer-owned pods of doers)
  fetchDoerGroups: async (): Promise<DoerGroupListItem[]> => {
    return api.get('/v1/admin/doer-groups').json<DoerGroupListItem[]>()
  },

  fetchDoerGroup: async (groupId: string): Promise<DoerGroupDetail> => {
    return api.get(`/v1/admin/doer-groups/${groupId}`).json<DoerGroupDetail>()
  },

  getLatestDslTeamSync: async (): Promise<DslTeamSyncLatest | null> => {
    return api.get('/v1/admin/dsl-team-sync/latest').json<DslTeamSyncLatest | null>()
  },

  createDoerGroup: async (input: CreateDoerGroupInput): Promise<DoerGroupListItem> => {
    return api.post(input, '/v1/admin/doer-groups').json<DoerGroupListItem>()
  },

  // Independent group chats. There is no member-facing management surface, so
  // the admin console is the only place membership is edited — including when
  // the admin is a member themselves.
  fetchGroupChats: async (includeArchived = false): Promise<GroupChatListItem[]> => {
    const query = includeArchived ? '?include_archived=true' : ''
    return api.get(`/v1/admin/group-chats${query}`).json<GroupChatListItem[]>()
  },

  fetchGroupChat: async (groupChatId: string): Promise<GroupChatListItem> => {
    return api.get(`/v1/admin/group-chats/${groupChatId}`).json<GroupChatListItem>()
  },

  createGroupChat: async (input: CreateGroupChatInput): Promise<GroupChatDetail> => {
    return api.post(input, '/v1/admin/group-chats').json<GroupChatDetail>()
  },

  updateGroupChat: async (groupChatId: string, input: UpdateGroupChatInput): Promise<GroupChatListItem> => {
    return api.patch(input, `/v1/admin/group-chats/${groupChatId}`).json<GroupChatListItem>()
  },

  addGroupChatMembers: async (groupChatId: string, memberIds: string[]): Promise<GroupChatListItem> => {
    return api
      .post({ member_ids: memberIds }, `/v1/admin/group-chats/${groupChatId}/members`)
      .json<GroupChatListItem>()
  },

  removeGroupChatMember: async (groupChatId: string, userId: string): Promise<GroupChatListItem> => {
    return api.delete(`/v1/admin/group-chats/${groupChatId}/members/${userId}`).json<GroupChatListItem>()
  },

  updateDoerGroup: async (groupId: string, input: UpdateDoerGroupInput): Promise<DoerGroupListItem> => {
    return api.patch(input, `/v1/admin/doer-groups/${groupId}`).json<DoerGroupListItem>()
  },

  deleteDoerGroup: async (groupId: string): Promise<void> => {
    await api.delete(`/v1/admin/doer-groups/${groupId}`).res()
  },

  addDoerGroupMember: async (groupId: string, doerId: string): Promise<DoerGroupMember> => {
    return api.post({ doer_id: doerId }, `/v1/admin/doer-groups/${groupId}/members`).json<DoerGroupMember>()
  },

  removeDoerGroupMember: async (groupId: string, doerId: string): Promise<void> => {
    await api.delete(`/v1/admin/doer-groups/${groupId}/members/${doerId}`).res()
  },

  // Category lifecycle. The code is immutable, so there is no rename — an
  // archived category keeps its history and can be restored.
  createCategory: async (input: FounderCategoryCreateInput): Promise<FounderCategory> => {
    return api.post(input, '/v1/admin/founders/categories').json<FounderCategory>()
  },

  updateCategory: async (code: string, input: FounderCategoryUpdateInput): Promise<FounderCategory> => {
    return api.patch(input, `/v1/admin/founders/categories/${code}`).json<FounderCategory>()
  },

  // Category recommended playbooks
  fetchCategoryWithPlaybooks: async (code: string): Promise<CategoryWithRecommendedPlaybooks> => {
    return api.get(`/v1/admin/founders/categories/${code}`).json<CategoryWithRecommendedPlaybooks>()
  },

  addCategoryPlaybooks: async (
    code: string,
    playbookIds: string[],
  ): Promise<CategoryRecommendedPlaybookWithPlaybook[]> => {
    return api
      .post({ playbook_ids: playbookIds }, `/v1/admin/founders/categories/${code}/playbooks`)
      .json<CategoryRecommendedPlaybookWithPlaybook[]>()
  },

  updateCategoryPlaybook: async (
    code: string,
    playbookId: string,
    updates: { is_optional?: boolean; display_order?: number },
  ): Promise<void> => {
    await api.patch(updates, `/v1/admin/founders/categories/${code}/playbooks/${playbookId}`).res()
  },

  removeCategoryPlaybook: async (code: string, playbookId: string): Promise<void> => {
    await api.delete(`/v1/admin/founders/categories/${code}/playbooks/${playbookId}`).res()
  },

  // Consolidated activity endpoint
  fetchUserActivity: async (userId: string, limit = 20): Promise<UserActivity> => {
    return api.get(`/v1/admin/users/${userId}/activity?limit=${limit}`).json<UserActivity>()
  },

  // Doer device registry (gated to admin + escalation_officer)
  fetchDoerDevices: async (doerId: string): Promise<AgentDeviceListResponse> => {
    return api.get(`/v1/admin/doers/${doerId}/devices`).json<AgentDeviceListResponse>()
  },

  /** Idempotent: revoking an already-revoked device returns it unchanged. */
  revokeDoerDevice: async (doerId: string, deviceId: string): Promise<AgentDeviceRevokeResponse> => {
    return api
      .post({}, `/v1/admin/doers/${doerId}/devices/${deviceId}/revoke`)
      .json<AgentDeviceRevokeResponse>()
  },

  /**
   * Idempotent: un-revoking an already-active device returns it unchanged.
   *
   * Takes a `reason` because the API requires one — un-revoke is the only path
   * back from a sticky revoke, so `audit_log` records why it was reversed and
   * by whom. `AdminDeviceUnrevokeBodySchema` is `.strict()` with `min(3)`, so
   * omitting it is a 400, not a silent default.
   */
  unrevokeDoerDevice: async (
    doerId: string,
    deviceId: string,
    reason: string,
  ): Promise<AgentDeviceRevokeResponse> => {
    return api
      .post({ reason }, `/v1/admin/doers/${doerId}/devices/${deviceId}/unrevoke`)
      .json<AgentDeviceRevokeResponse>()
  },

  // Founder onboarding overrides (admin only) — ClickUp 86d3ukp2h. Every one of
  // these takes a reason because the API requires one: it is what `audit_log`
  // records next to the acting admin.
  fetchFounderOnboarding: async (founderId: string): Promise<AdminOnboardingView> => {
    return api.get(`/v1/admin/users/${founderId}/onboarding`).json<AdminOnboardingView>()
  },

  /** Close a step on the founder's behalf. `skip` records it as skipped rather than done. */
  overrideOnboardingStep: async (
    founderId: string,
    step: OnboardingStepNumber,
    reason: string,
    skip = false,
  ): Promise<OnboardingTransitionResult> => {
    return api
      .post({ step, reason }, `/v1/admin/users/${founderId}/onboarding/${skip ? 'skip' : 'complete'}-step`)
      .json<OnboardingTransitionResult>()
  },

  /**
   * Record that this founder signed their agreement outside the system.
   *
   * Not `overrideOnboardingStep(founderId, 1, …)`: that closes the row, this
   * records the signature the activation gate and a later reset both read. Works
   * on a founder with no onboarding run at all — the API opens one.
   */
  markOnboardingAgreementSigned: async (
    founderId: string,
    reason: string,
  ): Promise<OnboardingTransitionResult> => {
    return api
      .post({ reason }, `/v1/admin/users/${founderId}/onboarding/agreement-signed`)
      .json<OnboardingTransitionResult>()
  },

  /** Archive the live run and open the next one. Destructive — hence the token. */
  resetFounderOnboarding: async (founderId: string, reason: string): Promise<AdminOnboardingResetResult> => {
    return api
      .post({ reason, confirm: ONBOARDING_RESET_CONFIRM }, `/v1/admin/users/${founderId}/onboarding/reset`)
      .json<AdminOnboardingResetResult>()
  },

  /** Re-drive (or, failing that, force) a step an integration should have closed. */
  unblockFounderOnboarding: async (
    founderId: string,
    reason: string,
    step?: OnboardingStepNumber,
  ): Promise<AdminOnboardingUnblockResult> => {
    return api
      .post({ reason, ...(step ? { step } : {}) }, `/v1/admin/users/${founderId}/onboarding/unblock`)
      .json<AdminOnboardingUnblockResult>()
  },
}
